import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME } from "../Variables.js";

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

// Sort by account code naturally
const sortByCode = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const CashFlowStatement = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(0);
    return date.toISOString().split('T')[0];
  });
  const [isFetching, setIsFetching] = useState(false);

  const accountsCollectionRef = collection(db, "accounts");
  const journalEntriesCollectionRef = collection(db, "journalEntries");

  useEffect(() => {
    if (!user || !userData) {
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
  }, [user, userData]);

  useEffect(() => {
    if (accounts.length > 0) {
      generateCashFlowStatement();
    }
  }, [startDate, endDate, accounts]);

  const fetchAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      setAccounts(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) {
      setError("Failed to fetch accounts");
      setShowModal(true);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid) return [];
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      return data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (err) {
      return [];
    }
  };

  const calculateBalanceAsOf = (accountId, accountName, entries, cutoffDate) => {
    let balance = 0;
    const cutoff = new Date(cutoffDate);
    entries.forEach(entry => {
      if (new Date(entry.date) <= cutoff) {
        entry.lines.forEach(line => {
          if (line.accountName === accountName || line.accountId === accountId) {
            balance += (Number(line.debit) || 0) - (Number(line.credit) || 0);
          }
        });
      }
    });
    return balance;
  };

  const classifyAccountForCashFlow = (account) => {
    const name = (account.name || '').toLowerCase();
    const type = (account.type || '').toLowerCase();

    if (name.includes('cash') || name.includes('bank')) return 'cash';
    if (type.includes('revenue') || type.includes('income') || name.includes('sales')) return 'operating';
    if (type.includes('expense') || name.includes('cost') || name.includes('operating')) return 'operating';
    if (name.includes('receivable') || name.includes('payable') || name.includes('inventory') || name.includes('prepayment')) return 'operating';
    if (name.includes('equipment') || name.includes('vehicle') || name.includes('property') || name.includes('building') || name.includes('fixed asset') || name.includes('investment')) return 'investing';
    if (name.includes('loan') || name.includes('capital') || name.includes('equity') || name.includes('drawing') || name.includes('dividend')) return 'financing';
    return 'operating'; // default
  };

  const generateCashFlowStatement = async () => {
    if (accounts.length === 0) return;
    setIsFetching(true);

    try {
      const entries = await fetchJournalEntries();

      const startBalances = {};
      const endBalances = {};

      accounts.forEach(acc => {
        startBalances[acc.id] = calculateBalanceAsOf(acc.id, acc.name, entries, startDate);
        endBalances[acc.id] = calculateBalanceAsOf(acc.id, acc.name, entries, endDate);
      });

      const operating = { inflows: [], outflows: [] };
      const investing = { inflows: [], outflows: [] };
      const financing = { inflows: [], outflows: [] };

      let cashAtBeginning = 0;
      let cashAtEnd = 0;

      accounts.forEach(acc => {
        if (!acc.active) return;
        const change = endBalances[acc.id] - startBalances[acc.id];
        if (Math.abs(change) < 0.01) return;

        const amount = Math.abs(change);
        const isInflow = change > 0;

        const category = classifyAccountForCashFlow(acc);

        if (category === 'cash') {
          cashAtBeginning += startBalances[acc.id];
          cashAtEnd += endBalances[acc.id];
          return;
        }

        const item = { ...acc, amount, isInflow };
        if (category === 'investing') {
          isInflow ? investing.inflows.push(item) : investing.outflows.push(item);
        } else if (category === 'financing') {
          isInflow ? financing.inflows.push(item) : financing.outflows.push(item);
        } else {
          isInflow ? operating.inflows.push(item) : operating.outflows.push(item);
        }
      });

      // Sort all
      [operating, investing, financing].forEach(cat => {
        cat.inflows.sort(sortByCode);
        cat.outflows.sort(sortByCode);
      });

      const netOperating = operating.inflows.reduce((s, i) => s + i.amount, 0) - operating.outflows.reduce((s, i) => s + i.amount, 0);
      const netInvesting = investing.inflows.reduce((s, i) => s + i.amount, 0) - investing.outflows.reduce((s, i) => s + i.amount, 0);
      const netFinancing = financing.inflows.reduce((s, i) => s + i.amount, 0) - financing.outflows.reduce((s, i) => s + i.amount, 0);
      const netIncrease = netOperating + netInvesting + netFinancing;

      setCashFlowData({
        activities: { operating, investing, financing },
        totals: {
          netOperating, netInvesting, netFinancing, netIncrease,
          cashAtBeginning, cashAtEnd: cashAtBeginning + netIncrease
        },
        period: { startDate, endDate }
      });

    } catch (err) {
      setError("Failed to generate cash flow statement");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const generatePDF = () => {
    if (!cashFlowData) return;
    const doc = new jsPDF();
    const { activities, totals, period } = cashFlowData;
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let y = 10;
    try { doc.addImage(ShopLogo, "PNG", 14, y, 15, 15); } catch (e) {}
    doc.setFontSize(16).setFont("helvetica", "bold").text(SHOP_NAME, 35, y + 5);
    doc.setFontSize(14).setFont("helvetica", "normal").text("Cash Flow Statement", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    doc.text(`For the period ${dateFormatter.format(new Date(period.startDate))} to ${dateFormatter.format(new Date(period.endDate))}`, 35, y + 28);
    y += 35;

    const tableData = [];

    tableData.push([{ content: 'CASH FLOW FROM OPERATING ACTIVITIES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
    activities.operating.inflows.forEach(i => tableData.push([i.name, `N${i.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]));
    activities.operating.outflows.forEach(o => tableData.push([o.name, `(N${o.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
    tableData.push(['NET CASH FROM OPERATING ACTIVITIES', { content: `N${totals.netOperating.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
    tableData.push(['', '']);

    tableData.push([{ content: 'CASH FLOW FROM INVESTING ACTIVITIES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
    activities.investing.inflows.forEach(i => tableData.push([i.name, `N${i.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]));
    activities.investing.outflows.forEach(o => tableData.push([o.name, `(N${o.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
    tableData.push(['NET CASH FROM INVESTING ACTIVITIES', { content: `N${totals.netInvesting.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
    tableData.push(['', '']);

    tableData.push([{ content: 'CASH FLOW FROM FINANCING ACTIVITIES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
    activities.financing.inflows.forEach(i => tableData.push([i.name, `N${i.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]));
    activities.financing.outflows.forEach(o => tableData.push([o.name, `(N${o.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
    tableData.push(['NET CASH FROM FINANCING ACTIVITIES', { content: `N${totals.netFinancing.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
    tableData.push(['', '']);

    tableData.push(['NET INCREASE IN CASH', { content: `N${totals.netIncrease.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
    tableData.push(['Cash at Beginning of Period', `N${totals.cashAtBeginning.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);
    tableData.push([
      { content: 'CASH AT END OF PERIOD', styles: { fontStyle: 'bold', fillColor: [220, 237, 200] } },
      { content: `N${totals.cashAtEnd.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold', fillColor: [220, 237, 200] } }
    ]);

    autoTable(doc, {
      startY: y,
      head: [['DESCRIPTION', 'AMOUNT']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, textColor: 0, },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 60, halign: 'right' } },
      margin: { left: 10, right: 14 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const text = data.cell.text.join('');
          if (text.includes('(')) data.cell.styles.textColor = [200, 0, 0];
          else if (text.startsWith('N')) data.cell.styles.textColor = [0, 100, 0];
        }
      }
    });

    doc.save(`CashFlowStatement_${startDate}_to_${endDate}.pdf`);
  };

  const refreshCashFlowStatement = () => {
    generateCashFlowStatement();
    setSuccessMessage("Cash Flow Statement refreshed successfully");
    setShowModal(true);
  };

  if (loading) return <Loader text="Loading" />;
  if (!userData) return <Loader text="Reading" />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  return (
    <>
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Cash Flow Statement</h2>
            <Link to="#" className="home-link" onClick={refreshCashFlowStatement}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            <div className="column gap-15 align-center justify-center" style={{ marginTop: '10px'}}>
              <div className="row gap-1">
                <div className="column align-center">
                  <label className="input-label bold">From Date</label>
                  <input
                    type="date"
                    className="input-field"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ marginBottom: '0px' }}
                  />
                </div>
                <div className="column align-center">
                  <label className="input-label bold">To Date</label>
                  <input
                    type="date"
                    className="input-field"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ marginBottom: '0px' }}
                  />
                </div>
              </div>
              <div className="row gap-1" style={{marginTop: '-25px', marginBottom: '10px'}}>
                <button 
                  className="login-button login-button-d caps" 
                  onClick={generatePDF}
                  disabled={!cashFlowData}
                >
                  DOWNLOAD
                </button>
              </div>
            </div>

            {isFetching ? (
              <p className="loading-text">Generating Cash Flow Statement...</p>
            ) : cashFlowData ? (
              <div className="cash-flow-container scroll-fixed scroll-fixed2">
                <div className={`cash-position ${cashFlowData.totals.netIncrease >= 0 ? 'positive' : 'negative'}`}>
                  <div className="row align-center gap-1">
                    <i className={`fas ${cashFlowData.totals.netIncrease >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                    <span className="bold">
                      {cashFlowData.totals.netIncrease >= 0 ? 'CASH INCREASE' : 'CASH DECREASE'}
                    </span>
                  </div>
                  <div className="cash-summary">
                    <div className="cash-item">
                      <span>Beginning Cash:</span>
                      <strong> N{cashFlowData.totals.cashAtBeginning.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                    <div className="cash-item">
                      <span>Net Change:</span>
                      <strong> {cashFlowData.totals.netIncrease >= 0 ? 'N' : '(N'}{Math.abs(cashFlowData.totals.netIncrease).toLocaleString('en-US', { minimumFractionDigits: 2 })}{cashFlowData.totals.netIncrease >= 0 ? '' : ')'}</strong>
                    </div>
                    <div className="cash-item total">
                      <span>Ending Cash:</span>
                      <strong> N{cashFlowData.totals.cashAtEnd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                  </div>
                </div>

                <div className="cash-flow-content">
                  {/* OPERATING */}
                  <div className="cash-flow-section operating">
                    <h3 className="cash-flow-header">CASH FROM OPERATING ACTIVITIES</h3>
                    {cashFlowData.activities.operating.inflows.map(i => (
                      <div key={i.id} className="cash-flow-item row space-between">
                        <span>{i.name}</span>
                        <span className="amount positive">N{i.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    {cashFlowData.activities.operating.outflows.map(o => (
                      <div key={o.id} className="cash-flow-item row space-between">
                        <span>{o.name}</span>
                        <span className="amount negative">(N{o.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                      </div>
                    ))}
                    <div className="cash-flow-total row space-between">
                      <strong>Net Cash from Operations</strong>
                      <strong className="amount">N{cashFlowData.totals.netOperating.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                  </div>

                  {/* INVESTING */}
                  {(cashFlowData.activities.investing.inflows.length > 0 || cashFlowData.activities.investing.outflows.length > 0) && (
                    <div className="cash-flow-section investing">
                      <h3 className="cash-flow-header">CASH FROM INVESTING ACTIVITIES</h3>
                      {cashFlowData.activities.investing.inflows.map(i => (
                        <div key={i.id} className="cash-flow-item row space-between">
                          <span>{i.name}</span>
                          <span className="amount positive">N{i.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                      {cashFlowData.activities.investing.outflows.map(o => (
                        <div key={o.id} className="cash-flow-item row space-between">
                          <span>{o.name}</span>
                          <span className="amount negative">(N{o.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                        </div>
                      ))}
                      <div className="cash-flow-total row space-between">
                        <strong>Net Cash from Investing</strong>
                        <strong className="amount">N{cashFlowData.totals.netInvesting.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>
                  )}

                  {/* FINANCING */}
                  {(cashFlowData.activities.financing.inflows.length > 0 || cashFlowData.activities.financing.outflows.length > 0) && (
                    <div className="cash-flow-section financing">
                      <h3 className="cash-flow-header">CASH FROM FINANCING ACTIVITIES</h3>
                      {cashFlowData.activities.financing.inflows.map(i => (
                        <div key={i.id} className="cash-flow-item row space-between">
                          <span>{i.name}</span>
                          <span className="amount positive">N{i.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                      {cashFlowData.activities.financing.outflows.map(o => (
                        <div key={o.id} className="cash-flow-item row space-between">
                          <span>{o.name}</span>
                          <span className="amount negative">(N{o.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                        </div>
                      ))}
                      <div className="cash-flow-total row space-between">
                        <strong>Net Cash from Financing</strong>
                        <strong className="amount">N{cashFlowData.totals.netFinancing.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>
                  )}

                  <div className="cash-flow-summary">
                    <div className="cash-flow-total row space-between">
                      <strong>Net Increase in Cash</strong>
                      <strong className="amount">N{cashFlowData.totals.netIncrease.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                    <div className="cash-flow-item row space-between">
                      <span>Cash at Beginning</span>
                      <span className="amount">N{cashFlowData.totals.cashAtBeginning.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="cash-flow-grand-total row space-between">
                      <strong>CASH AT END</strong>
                      <strong className="amount">N{cashFlowData.totals.cashAtEnd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="loading-text">No cash flow data for the selected period.</p>
            )}
          </div>

          {showModal && (
            <SuccessModal
              icon={error ? "xmark" : "check"}
              iconColor={error ? "red" : "green"}
              message={error || successMessage}
              onClose={() => {
                setError("");
                setSuccessMessage("");
                setShowModal(false);
              }}
            />
          )}
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      <style jsx>{`
        .cash-position {
          padding: 15px;
          border-radius: 8px;
          margin: 10px 0;
          text-align: center;
          width: 100%;
        }
        .cash-position.positive {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .cash-position.negative {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        .cash-summary {
          margin-top: 10px;
        }
        .cash-item {
          display: flex;
          justify-content: space-between;
          margin: 5px 0;
        }
        .cash-item.total {
          border-top: 1px solid;
          padding-top: 8px;
          margin-top: 8px;
          font-size: 16px;
        }
        .cash-flow-container {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
        }
        .cash-flow-content {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .cash-flow-section {
          margin-bottom: 25px;
          padding: 15px;
          border-radius: 6px;
        }
        .cash-flow-section.operating {
          background-color: #e8f4fd;
          border-left: 4px solid #3498db;
        }
        .cash-flow-section.investing {
          background-color: #e8f8f0;
          border-left: 4px solid #27ae60;
        }
        .cash-flow-section.financing {
          background-color: #fef9e7;
          border-left: 4px solid #f39c12;
        }
        .cash-flow-header {
          color: #2c3e50;
          margin-bottom: 15px;
          font-size: 16px;
        }
        .cash-flow-item {
          padding: 8px 0;
          border-bottom: 1px dotted #dee2e6;
        }
        .cash-flow-total {
          padding: 12px 0;
          border-top: 2px solid #adb5bd;
          margin-top: 10px;
        }
        .cash-flow-grand-total {
          padding: 15px 0;
          border-top: 3px double #2c3e50;
          margin-top: 15px;
          font-size: 16px;
          background-color: #d4edda;
          padding: 15px;
          border-radius: 4px;
        }
        .cash-flow-summary {
          background-color: #f8f9fa;
          padding: 20px;
          border-radius: 6px;
          margin-top: 20px;
        }
        .amount {
          font-family: monospace;
          font-weight: bold;
        }
        .amount.positive {
          color: #155724;
        }
        .amount.negative {
          color: #721c24;
        }
        @media (max-width: 768px) {
          .cash-flow-content { padding: 15px; }
          .cash-flow-section { padding: 10px; }
        }
      `}</style>
    </>
  );
};

export default CashFlowStatement;